import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { AuthContext } from '../App';
import Navbar from '../components/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users, Trophy, ShoppingBag, MessageCircle, TrendingUp, Flame } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

export default function AdminStatsPage() {
  const { API, user } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    axios.get(`${API}/admin/stats/overview`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setStats(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (!user?.is_admin) {
    return (
      <div className="min-h-screen bg-slate-50" dir="rtl">
        <Navbar />
        <p className="text-center py-20 text-gray-500">גישה לאדמין בלבד</p>
      </div>
    );
  }

  const totalCards = stats ? [
    { label: 'שחקנים רשומים', value: stats.totals.users, icon: Users, color: 'from-blue-500 to-cyan-500', week: `+${stats.this_week.new_users} השבוע` },
    { label: 'תחרויות', value: stats.totals.tournaments, icon: Trophy, color: 'from-amber-500 to-orange-500', week: `${stats.this_week.registrations} הרשמות השבוע` },
    { label: 'משחקים שהושלמו', value: stats.totals.matches_completed, icon: TrendingUp, color: 'from-emerald-500 to-teal-500', week: `+${stats.this_week.matches_completed} השבוע` },
    { label: 'מודעות ציוד', value: stats.totals.gear_posts, icon: ShoppingBag, color: 'from-purple-500 to-indigo-500', week: `+${stats.this_week.gear_posts} השבוע` },
    { label: 'הודעות צ׳אט', value: stats.totals.messages, icon: MessageCircle, color: 'from-pink-500 to-rose-500', week: '' },
  ] : [];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950" dir="rtl" data-testid="admin-stats-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="text-3xl sm:text-4xl font-black text-gray-900 dark:text-white mb-1">📊 דשבורד סטטיסטיקות</h1>
        <p className="text-gray-500 mb-8">תמונת מצב חיה של פעילות האתר</p>

        {loading ? (
          <p className="text-center py-16 text-gray-400">טוען נתונים...</p>
        ) : !stats ? (
          <p className="text-center py-16 text-gray-400">שגיאה בטעינת הנתונים</p>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
              {totalCards.map((c) => (
                <Card key={c.label} className="overflow-hidden" data-testid={`stat-card-${c.label}`}>
                  <CardContent className="p-4">
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${c.color} flex items-center justify-center mb-3 shadow`}>
                      <c.icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="text-3xl font-black text-gray-900 dark:text-white">{c.value}</div>
                    <div className="text-xs text-gray-500 font-semibold">{c.label}</div>
                    {c.week && <div className="text-[11px] text-emerald-600 font-bold mt-1">{c.week}</div>}
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              <Card data-testid="chart-users-by-week">
                <CardHeader><CardTitle className="text-lg">👥 שחקנים חדשים — 8 שבועות אחרונים</CardTitle></CardHeader>
                <CardContent className="h-56" dir="ltr">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats.users_by_week}>
                      <XAxis dataKey="week" fontSize={11} />
                      <YAxis allowDecimals={false} fontSize={11} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#3b82f6" radius={[6, 6, 0, 0]} name="שחקנים" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
              <Card data-testid="chart-matches-by-week">
                <CardHeader><CardTitle className="text-lg">🎾 משחקים שהושלמו — 8 שבועות אחרונים</CardTitle></CardHeader>
                <CardContent className="h-56" dir="ltr">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats.matches_by_week}>
                      <XAxis dataKey="week" fontSize={11} />
                      <YAxis allowDecimals={false} fontSize={11} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#10b981" radius={[6, 6, 0, 0]} name="משחקים" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            <Card data-testid="filling-fast-table">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Flame className="w-5 h-5 text-orange-500" />
                  תחרויות שמתמלאות מהר
                </CardTitle>
              </CardHeader>
              <CardContent>
                {stats.filling_fast.length === 0 ? (
                  <p className="text-gray-400 text-sm">אין תחרויות פתוחות כרגע</p>
                ) : (
                  <div className="space-y-3">
                    {stats.filling_fast.map((t) => (
                      <Link key={t.id} to={`/tournament/${t.id}`} className="block" data-testid={`filling-row-${t.id}`}>
                        <div className="flex items-center gap-3 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-xl p-2 transition-colors">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-bold text-gray-800 dark:text-gray-100 text-sm truncate">{t.name}</span>
                              <Badge variant="outline" className="text-[10px]">{t.category}</Badge>
                              {t.waiting > 0 && <Badge className="bg-amber-500 text-white text-[10px]">{t.waiting} בהמתנה</Badge>}
                            </div>
                            <div className="mt-1.5 h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full ${t.fill_pct >= 90 ? 'bg-red-500' : t.fill_pct >= 60 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                                style={{ width: `${Math.min(100, t.fill_pct)}%` }}
                              ></div>
                            </div>
                          </div>
                          <div className="text-left shrink-0">
                            <div className="font-black text-gray-900 dark:text-white">{t.registered}/{t.max_players}</div>
                            <div className="text-[11px] text-gray-400">{t.fill_pct}%</div>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
